import { policiesContext, WidgetConstants, serviceLineMapper } from "../../utils/common";
import { Box, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import gear from "../../../public/assets/icons/gear.svg";
import WorkItemTable from "../organisms/workItemsTable";
import React, { useCallback, useEffect, useRef, useState } from "react";
import QualityAssessmentSettings from "../molecules/QualityAssessmentSettings";
import AuditStatusChip from "../molecules/AuditStatusChip";
import { getAvailableStatuses, getUserRolesFromActiveContext, createProjectItemFromQaRule } from "../molecules/ProjectSelectColumn";
import { WorkitemStatus, CollectionWithWorkItemCounts } from "../molecules/ProjectSelectColumn/types";
import { fetchProjectsWithCounts } from "../molecules/ProjectSelectColumn/utilsProjectSelect";
import { GraderCountsRow } from "../molecules/GraderCountsRow";
import { useHistory, useLocation } from "react-router-dom";
import theme from "../../theme/Theme";
import { isMultiUserMode } from "../../utils/cqa/assignmentUtil";
import { QueryWorkers } from "../../queries/Workers";
import { getQaRules, apiGetAuditSample, AuditSampleResponse } from "../../utils/api";
import { SnackbarContext } from "../../context/AlertsContext";

interface IProps {
    client: any;
    widgetOptions: any;
    directoryType: string;
}

export const buildCqaStatusFilters = (
    baseFilters: any[],
    workitemStatus: WorkitemStatus | null,
    availableStatuses: WorkitemStatus[],
    isCqaManager: boolean
): any[] => {
    const withoutStatus = (baseFilters ?? []).filter(
        (f: any) => f.key !== "qa_status" && f.key !== "workitem.qa_status"
    );
    if (workitemStatus) {
        if (workitemStatus === WorkitemStatus.ASSIGNED_TO_ME) {
            if (isCqaManager) {
                return [
                    ...withoutStatus,
                    { value: "MY_CASES", key: "assignee", operation: "eq" },
                    { value: WorkitemStatus.IN_CQA, key: "workitem.qa_status", operation: "eq" },
                ];
            }
            return withoutStatus;
        }
        if (workitemStatus === WorkitemStatus.CQA_APPROVED) {
            // The Approved tab spans direct approvals and audit-graded items.
            return [
                ...withoutStatus,
                {
                    value: [WorkitemStatus.CQA_APPROVED, WorkitemStatus.CQA_AUDIT_COMPLETE],
                    key: "workitem.qa_status",
                    operation: "in",
                },
            ];
        }
        return [...withoutStatus, { value: workitemStatus, key: "workitem.qa_status", operation: "eq" }];
    }
    return [
        ...withoutStatus,
        {
            value: availableStatuses.filter(
                (s) => s !== WorkitemStatus.ASSIGNED_TO_ME && s !== WorkitemStatus.CQA_CLOSED
            ),
            key: "workitem.qa_status",
            operation: "in",
        },
    ];
};

const QualityAssessment = (props: IProps) => {
    const { client, widgetOptions, directoryType } = props;
    const [qualityAssessmentSettingsOpen, setQualityAssessmentSettingsOpen] = React.useState(false);
    const { roles, userContext } = React.useContext(policiesContext);
    const { openSnackbar } = React.useContext(SnackbarContext);
    const availableStatuses = getAvailableStatuses(userContext);
    const history = useHistory();
    const location = useLocation();

    const [selectedProject, setSelectedProject] = React.useState<CollectionWithWorkItemCounts>(null);
    const [workitemStatus, setWorkitemStatus] = React.useState<WorkitemStatus | null>(null);
    const [groupByCQAWorkItemId, setGroupByCQAWorkItemId] = React.useState(false);
    const [countIsLoading, setCountIsLoading] = React.useState(false);
    const [isLoadingProject, setIsLoadingProject] = React.useState(false);
    const [activeSample, setActiveSample] = useState<AuditSampleResponse | null>(null);

    const workerIdRef = useRef<string>('');
    const [workerIdState, setWorkerIdState] = React.useState<string>('');
    const isFetchingProject = useRef(false);

    useEffect(() => {
        if (!userContext?.user?.userId) return;
        client.mutate({
            mutation: QueryWorkers,
            variables: {
                workerQuery: {
                    query: `Select * from worker where user_id='${userContext.user.userId}' and subtenant_id = '${userContext.activeContext.tenantId}' and status='ACTIVE';`
                }
            }
        }).then((response: any) => {
            if (response.data?.QueryWorker?.length) {
                const id = response.data.QueryWorker[0].worker_id;
                workerIdRef.current = id;
                setWorkerIdState(id);
            }
        }).catch((err: any) => {
            console.error("Error fetching worker:", err);
        });
    }, [userContext?.user?.userId]);

    const refreshActiveSample = useCallback(async () => {
        if (!selectedProject?.collection_id) {
            setActiveSample(null);
            return;
        }
        try {
            const sample = await apiGetAuditSample(selectedProject.collection_id);
            setActiveSample(sample);
        } catch {
            setActiveSample(null);
        }
    }, [selectedProject?.collection_id]);

    useEffect(() => {
        refreshActiveSample();
    }, [refreshActiveSample]);

    useEffect(() => {
        const handler = () => { refreshActiveSample(); };
        window.addEventListener('workitems-updated', handler);
        return () => window.removeEventListener('workitems-updated', handler);
    }, [refreshActiveSample]);

    useEffect(() => {
        const handleAuditClosed = (e: Event) => {
            const detail = (e as CustomEvent).detail as { status: string } | null;
            refreshActiveSample();
            if (detail === null) {
                openSnackbar('Audit complete — all items reviewed', 'success');
            } else if (detail.status === 'PASSED') {
                openSnackbar('Audit passed', 'success');
            }
        };
        window.addEventListener('audit-closed', handleAuditClosed);
        return () => window.removeEventListener('audit-closed', handleAuditClosed);
    }, [openSnackbar, refreshActiveSample]);

    // Load the tab counts for the selected project. Kept in a ref so the (register-once) event
    // listener below always invokes the latest closure without re-subscribing on every count update.
    const loadCountsRef = React.useRef<(opts?: { silent?: boolean }) => void>(() => {});
    // Monotonic id so overlapping refetches (the retry fan-out below) can't let a slow,
    // stale response overwrite a newer one — only the latest-started fetch applies its result.
    const countsGenRef = React.useRef(0);
    loadCountsRef.current = ({ silent = false }: { silent?: boolean } = {}) => {
        if (!selectedProject?.collection_id || !workerIdState) return;

        const activeContextRoles = getUserRolesFromActiveContext(userContext);
        const gen = ++countsGenRef.current;
        // Background retries don't toggle the loading flag (avoids badge flicker mid-poll).
        if (!silent) setCountIsLoading(true);

        fetchProjectsWithCounts(
            [selectedProject.collection_id],
            [selectedProject],
            activeContextRoles,
            workerIdState,
            client,
            false
        ).then((projectsWithCounts) => {
            if (gen !== countsGenRef.current) return; // superseded by a newer refetch
            if (projectsWithCounts.length > 0) {
                setSelectedProject(projectsWithCounts[0]);
            }
        }).catch((err: any) => {
            console.error('Error fetching work item counts:', err);
        }).finally(() => {
            if (!silent && gen === countsGenRef.current) setCountIsLoading(false);
        });
    };

    useEffect(() => {
        loadCountsRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject?.collection_id, workerIdState]);

    // Refetch the TAB counts after a workitem action (force-close / revert / approve). Without this,
    // selectedProject.workItemCounts only refreshes on project change, so the "Remaining Cases (N)"
    // badges stay stale after a force-close until a full page reload. cqa-service updates the directory
    // (the count source) ASYNC over kafka (cqa-update-workitem), so retry over ~6s until it settles.
    useEffect(() => {
        let timers: ReturnType<typeof setTimeout>[] = [];
        const onWorkItemsUpdated = () => {
            // Cancel any in-flight retry wave so rapid actions don't stack overlapping polls.
            timers.forEach(clearTimeout);
            timers = [500, 1500, 3000, 6000].map((ms) =>
                setTimeout(() => loadCountsRef.current({ silent: true }), ms)
            );
        };
        window.addEventListener('workitems-updated', onWorkItemsUpdated);
        return () => {
            window.removeEventListener('workitems-updated', onWorkItemsUpdated);
            timers.forEach(clearTimeout);
        };
    }, []);

    // Sync state from URL — reacts when dashboard-ui sidebar updates ?projectId=X&status=Y
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const newProjectId = params.get('projectId');
        const newStatus = params.get('status') as WorkitemStatus | null;

        setWorkitemStatus(newStatus || null);

        if (!newProjectId) {
            setSelectedProject(null);
            return;
        }

        if (selectedProject?.collection_id === newProjectId) {
            return;
        }

        if (isFetchingProject.current) return;

        const serviceLine = serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name;
        if (!serviceLine) return;

        isFetchingProject.current = true;
        setIsLoadingProject(true);
        getQaRules(client, 'cqa_samples', serviceLine, [])
            .then((rules: any[]) => {
                if (!rules) return;
                const activeContextRoles = getUserRolesFromActiveContext(userContext);
                const matchingRule = rules.find((rule: any) => rule.data.collection_id === newProjectId);
                if (matchingRule) {
                    const project = createProjectItemFromQaRule(matchingRule, activeContextRoles, workerIdRef.current);
                    setSelectedProject(project as CollectionWithWorkItemCounts);
                }
            })
            .catch((err: any) => {
                console.error("Error fetching project by ID:", err);
            })
            .finally(() => {
                isFetchingProject.current = false;
                setIsLoadingProject(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const handleStatusChange = (status: WorkitemStatus | null) => {
        const params = new URLSearchParams(location.search);
        if (selectedProject?.collection_id) {
            params.set('projectId', selectedProject.collection_id);
        }
        if (status) {
            params.set('status', status);
        } else {
            params.delete('status');
        }
        history.replace({ search: params.toString(), state: location.state });
    };

    useEffect(() => {
        const multiUserMode = isMultiUserMode(selectedProject?.assignee_ids, selectedProject?.project_configuration);
        const groupForManager =
            roles?.includes("cqa_manager") &&
            multiUserMode &&
            workitemStatus !== WorkitemStatus.ASSIGNED_TO_ME;
        setGroupByCQAWorkItemId(groupForManager);
    }, [roles, selectedProject, workitemStatus]);

    let filtersWithStatus: any[] = [];
    if (selectedProject?.filters) {
        filtersWithStatus = buildCqaStatusFilters(
            selectedProject.filters,
            workitemStatus,
            availableStatuses,
            !!roles?.includes("cqa_manager")
        );
    }

    const multiUserMode = isMultiUserMode(selectedProject?.assignee_ids, selectedProject?.project_configuration);
    const graderCountsRow = roles?.includes("cqa_manager") && selectedProject && multiUserMode
        ? (<GraderCountsRow client={client} project={selectedProject} />)
        : null;

    const effectiveAvailableStatuses = availableStatuses.filter(
        (s) => (s !== WorkitemStatus.CQA_AUDIT && s !== WorkitemStatus.CQA_AUDIT_COMPLETE) || !!activeSample
    );

    if (!directoryType) return <></>;
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100vh',
            overflow: 'hidden'
        }}>
            <Box sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                width: '100%',
                padding: '2.5rem',
            }}>
                {isLoadingProject && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                    }}>
                        <CircularProgress size={80} />
                    </Box>
                )}
                {selectedProject && (
                    <WorkItemTable
                        DirectoryType={directoryType}
                        isQualityAssessment={true}
                        widgetType={WidgetConstants.CQA}
                        widgetTitle={
                            <Typography variant="h2" component="span">
                                {WidgetConstants.QUALITY_ASSESSMENT}
                                {selectedProject?.name && (
                                    <>
                                        <span> /</span>
                                        <span style={{ color: theme.palette.artisticTextHigh.main }}>
                                            {" "}
                                            {selectedProject.name}
                                        </span>
                                    </>
                                )}
                            </Typography>
                        }
                        cqaWorkitemStatus={workitemStatus}

                        widgetTitleActions={
                            <Grid container justifyContent="flex-end" alignItems="center">
                                {roles?.includes("cqa_manager") ? (
                                    <>
                                        <AuditStatusChip sample={activeSample} />
                                        <IconButton onClick={() => setQualityAssessmentSettingsOpen(true)}>
                                            <img src={gear} alt="Settings" />
                                        </IconButton>
                                    </>
                                ) : null}
                            </Grid>
                        }

                            client={client}
                        handleRowClick={(workitem: any, data: any) => {
                                widgetOptions?.onWorkItemSelected(workitem, {
                                    ...data,
                                    isQualityAssessment: true,
                                    selectedProjectId: selectedProject?.collection_id,
                                    selectedProjectName: selectedProject?.name,
                                    selectedStatus: workitemStatus,
                                });
                            }}

                        overridingViewFilters={filtersWithStatus as any}
                        groupByCQAWorkItemId={groupByCQAWorkItemId}
                        handleViewWorkitemData={(workitem: any) => widgetOptions?.viewWorkitemData(workitem)}
                        GraderCountsRow={graderCountsRow}
                        shouldLoadData={!!selectedProject}
                        projectIdentifier={selectedProject ? `${selectedProject.collection_id}-${workitemStatus || 'all'}` : undefined}
                        selectedProject={selectedProject}
                        selectedStatus={workitemStatus}
                        availableStatuses={effectiveAvailableStatuses}
                        onStatusChange={handleStatusChange}
                        countIsLoading={countIsLoading}
                    />
                )}
                <QualityAssessmentSettings isOpen={qualityAssessmentSettingsOpen} onClose={() => setQualityAssessmentSettingsOpen(false)} client={client} />
            </Box>
        </Box>
    );
}

export default QualityAssessment;
